/**
 * MilestoneEditPopover.jsx — inline milestone editor for the Deal Rooms list.
 *
 * Edits the stage milestone's target date and note only, saved through the
 * canonical POST /api/v2/deals/{id}/milestones/{mid}/target and /note endpoints
 * (same as the Deal Room). No status / completion edits and no local-only state:
 * progression is recorded in the Deal Room. Errors keep the popover open.
 */

import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../ui/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from "../ui/sheet";
import { useAuth } from "../../data/authStore";
import { useChainStore, getDealExchangeTarget, getDealCompletionTarget } from "../../data/chainStore";

export const MILESTONE_STATUSES = [
  { id: "not_started",  label: "Not started" },
  { id: "in_progress",  label: "In progress" },
  { id: "blocked",      label: "Blocked"     },
  { id: "complete",     label: "Complete"    },
];

export const MILESTONE_OWNERS = [
  { id: "agent",            label: "Agent"             },
  { id: "buyer_solicitor",  label: "Buyer solicitor"   },
  { id: "seller_solicitor", label: "Seller solicitor"  },
  { id: "broker",           label: "Broker"            },
  { id: "client",           label: "Client"            },
  { id: "other",            label: "Other"             },
];

/* Local hook — track viewport width so we can pick popover vs sheet
   without dragging in the whole `useMediaQuery` doctrine. 768px is
   the Tailwind `md` breakpoint. */
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });
  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

/* List stage label → canonical milestone stage id (same ids the Deal Room uses). */
const STAGE_FOR_LABEL = {
  Mortgage: "mortgage", Contract: "contract", "Mgmt Pack": "mgmt_pack", "Management Pack": "mgmt_pack",
  Survey: "survey", Enquiries: "enquiries", Exchange: "exchange", Completion: "complete",
};
// Same terminal milestones as MilestoneProgressionSheet; otherwise the stage's last required milestone.
const TERMINAL_MILESTONES = { exchange: "contracts_exchanged", complete: "completion_date" };
// Mirrors backend INTERNAL_AGENT_ROLES; the server enforces this independently (403).
const EDIT_ROLES = new Set(["admin", "director", "manager", "sales_progressor", "senior_sales_negotiator", "sales_negotiator", "negotiator"]);
const pickStageMilestone = (items, stage) => {
  const inStage = (items || []).filter((m) => m.stage === stage);
  return inStage.find((m) => m.id === TERMINAL_MILESTONES[stage])
    || [...inStage].reverse().find((m) => !m.optional && !m.conditional)
    || inStage[inStage.length - 1] || null;
};

/* Internal form body — used by both the desktop popover and the
   mobile sheet. Saves target date + note through the canonical
   milestone endpoints; nothing is stored locally. */
function MilestoneEditForm({ dealId, stageId, stageLabel, onCancel, onClose }) {
  const { hydrateDeals, deals, updateDeal } = useChainStore();
  const { user } = useAuth();
  const canEdit = EDIT_ROLES.has(String(user?.role || "").toLowerCase());
  const stage = STAGE_FOR_LABEL[stageId] || null;
  // Exchange / Completion targets are the deal's own (header, Dates column, Forecasting).
  const dealTargetKey = stage === "exchange" ? "targetExchange" : stage === "complete" ? "targetCompletion" : null;
  const deal = (deals || []).find((d) => d.id === dealId);
  const dealTarget = dealTargetKey
    ? String((stage === "exchange" ? getDealExchangeTarget(deal) : getDealCompletionTarget(deal)) || "").slice(0, 10)
    : null;
  const [milestone, setMilestone] = useState(null);
  const [loadState, setLoadState] = useState(stage ? "loading" : "unsupported");
  const [target, setTarget] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!stage) return undefined;
    let alive = true;
    api.get(`/api/v2/deals/${dealId}/milestones`)
      .then(({ data }) => {
        if (!alive) return;
        const m = pickStageMilestone(data?.items, stage);
        setMilestone(m);
        setTarget(dealTargetKey ? dealTarget : (m?.target_date ? String(m.target_date).slice(0, 10) : ""));
        setNote(m?.note || "");
        setLoadState(m ? "ready" : "unsupported");
      })
      .catch(() => alive && setLoadState("error"));
    return () => { alive = false; };
  }, [dealId, stage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = useCallback(async () => {
    if (!milestone) return;
    setSaving(true);
    setError("");
    const base = `/api/v2/deals/${dealId}/milestones/${milestone.id}`;
    const prevTarget = dealTargetKey ? dealTarget : (milestone.target_date ? String(milestone.target_date).slice(0, 10) : "");
    let targetSaved = false;
    try {
      if (target !== prevTarget) {
        if (dealTargetKey) {
          await updateDeal(dealId, { [dealTargetKey]: target || null });
        } else {
          await api.post(`${base}/target`, { target_date: target || null });
          setMilestone((m) => ({ ...m, target_date: target || null }));
        }
        targetSaved = true;
      }
      if (note.trim() !== (milestone.note || "")) await api.post(`${base}/note`, { note: note.trim() });
      toast.success("Milestone saved");
      try { await hydrateDeals(); } catch (_e) { /* reconciliation is best-effort */ }
      onClose?.();
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Please try again.";
      setError(targetSaved ? `Target date saved, but the note couldn't be saved. ${msg}` : `Couldn't save — nothing was changed. ${msg}`);
    } finally {
      setSaving(false);
    }
  }, [dealId, milestone, target, note, hydrateDeals, onClose, dealTargetKey, dealTarget, updateDeal]);

  const fieldCls = "rounded-lg border border-slate-200 bg-white px-3 py-2 max-md:min-h-[44px] max-md:text-[16px] text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-violet-300";
  return (
    <div
      className="flex flex-col gap-3 p-4 w-full md:w-auto md:min-w-[280px] md:max-w-[320px]"
      data-testid={`dr-milestone-edit-form-${dealId}-${stageId}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-400">Milestone</div>
        <div className="text-[13px] font-bold text-slate-900 truncate" title={stageLabel}>{stageLabel}</div>
      </div>

      {loadState === "loading" && <p className="text-[12.5px] text-slate-500" data-testid={`dr-milestone-loading-${dealId}-${stageId}`}>Loading saved milestone…</p>}
      {loadState === "error" && <p className="text-[12.5px] text-rose-600" data-testid={`dr-milestone-load-error-${dealId}-${stageId}`}>{"Couldn't load this milestone. Open the Deal Room to edit it."}</p>}
      {loadState === "unsupported" && <p className="text-[12.5px] text-slate-500" data-testid={`dr-milestone-unsupported-${dealId}-${stageId}`}>Edit this stage in the Deal Room.</p>}

      {loadState === "ready" && (
        <>
          <p className="text-[11.5px] text-slate-500 leading-snug" data-testid={`dr-milestone-target-name-${dealId}-${stageId}`}>
            {milestone.label}
          </p>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-500">{dealTargetKey ? (stage === "exchange" ? "Exchange target date" : "Completion target date") : "Milestone target date"}</label>
            <input type="date" value={target} onChange={(e) => setTarget(e.target.value)} className={`${fieldCls} disabled:bg-slate-50 disabled:text-slate-600`} disabled={!canEdit}
              data-testid={`dr-milestone-target-${dealId}-${stageId}`} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-500">Update note</label>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Short update…"
              readOnly={!canEdit} className={`${fieldCls} resize-none ${canEdit ? "" : "bg-slate-50 text-slate-600"}`} data-testid={`dr-milestone-note-${dealId}-${stageId}`} />
          </div>
        </>
      )}

      {loadState === "ready" && !canEdit && (
        <p className="text-[11.5px] text-slate-500" data-testid={`dr-milestone-readonly-${dealId}-${stageId}`}>{"View only — your role can't edit dates or notes."}</p>
      )}
      {error && <p className="text-[12px] text-rose-600" role="alert" data-testid={`dr-milestone-save-error-${dealId}-${stageId}`}>{error}</p>}

      <div className="flex items-center justify-between gap-2 pt-1">
        <Link
          to={`/deal-rooms/${dealId}`}
          className="text-[12.5px] font-semibold text-violet-700 hover:text-violet-800 max-md:py-3"
          data-testid={`dr-milestone-open-room-${dealId}-${stageId}`}
        >
          Open in Deal Room
        </Link>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onCancel}
            className="inline-flex items-center justify-center rounded-lg bg-white hover:bg-slate-50 ring-1 ring-slate-200 text-slate-700 text-[12.5px] font-semibold px-3 py-1.5 max-md:min-h-[44px] max-md:px-4"
            data-testid={`dr-milestone-cancel-${dealId}-${stageId}`}>{canEdit ? "Cancel" : "Close"}</button>
          {loadState === "ready" && canEdit && (
            <button type="button" onClick={handleSave} disabled={saving}
              className="inline-flex items-center justify-center rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[12.5px] font-semibold px-3 py-1.5 max-md:min-h-[44px] max-md:px-5 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid={`dr-milestone-save-${dealId}-${stageId}`}>{saving ? "Saving…" : "Save"}</button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Wraps a milestone trigger element. Clicking the child opens a
 * popover (desktop) or bottom sheet (mobile) carrying the
 * milestone edit form.
 *
 * Props:
 *   dealId      — string
 *   stageId     — string (the canonical stage label, e.g. "Survey")
 *   stageLabel  — display label
 *   children    — the milestone dot / node (rendered inside the
 *                 PopoverTrigger / SheetTrigger).
 */
export function MilestoneEditor({ dealId, stageId, stageLabel, children }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  const formProps = useMemo(() => ({
    dealId, stageId, stageLabel,
    onCancel: () => setOpen(false),
    onClose: () => setOpen(false),
  }), [dealId, stageId, stageLabel]);

  if (isMobile) {
    // Wrapper stops portal clicks (overlay dismiss, sheet body) bubbling to the card's link.
    return (
      <span className="contents" onClick={(e) => e.stopPropagation()}>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            className="appearance-none bg-transparent border-0 p-3 -m-3 cursor-pointer touch-manipulation"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
            data-testid={`dr-milestone-trigger-${dealId}-${stageId}`}
            aria-label={`Edit milestone ${stageLabel}`}
          >
            {children}
          </button>
        </SheetTrigger>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl [&>button:first-child]:right-[2px] [&>button:first-child]:top-[2px] [&>button:first-child]:h-11 [&>button:first-child]:w-11 [&>button:first-child]:inline-flex [&>button:first-child]:items-center [&>button:first-child]:justify-center [&>button:first-child]:data-[state=open]:bg-transparent"
          onClick={(e) => e.stopPropagation()}
          data-testid={`dr-milestone-sheet-${dealId}-${stageId}`}
        >
          <SheetHeader className="text-left">
            <SheetTitle className="text-[15px] font-bold text-slate-900">
              Edit milestone
            </SheetTitle>
            <SheetDescription className="text-[12px] text-slate-500">
              Update the target date or note without leaving the list.
            </SheetDescription>
          </SheetHeader>
          <MilestoneEditForm {...formProps} />
        </SheetContent>
      </Sheet>
      </span>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="appearance-none bg-transparent border-0 p-0 m-0 cursor-pointer"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
          data-testid={`dr-milestone-trigger-${dealId}-${stageId}`}
          aria-label={`Edit milestone ${stageLabel}`}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-auto"
        side="bottom"
        align="center"
        sideOffset={8}
        data-testid={`dr-milestone-popover-${dealId}-${stageId}`}
      >
        <MilestoneEditForm {...formProps} />
      </PopoverContent>
    </Popover>
  );
}

export default MilestoneEditor;
